import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  bg:      '#FFFFFF',
  bgCard:  '#FAFAFA',
  border:  '#E4E4E7',
  fg:      '#09090B',
  fgMid:   '#52525B',
  fgMuted: '#A1A1AA',
  green:   '#059669',
  amber:   '#D97706',
};

const VERDICT = {
  A:    { label: 'A WINS', color: T.fg,    bg: '#F9FAFB',  border: '#E5E7EB' },
  B:    { label: 'B WINS', color: T.amber, bg: '#FFFBEB',  border: '#FDE68A' },
  TIED: { label: 'TOO CLOSE', color: T.fgMid, bg: '#F3F4F6', border: '#E5E7EB' },
};

function Skeleton() {
  return (
    <div style={{
      padding: '14px 16px',
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{ width: 52, height: 18, background: '#F4F4F5', borderRadius: 4 }} />
      <div style={{ flex: 1 }}>
        <div style={{ width: '60%', height: 12, background: '#F4F4F5', borderRadius: 3, marginBottom: 6 }} />
        <div style={{ width: '35%', height: 10, background: '#F4F4F5', borderRadius: 3 }} />
      </div>
    </div>
  );
}

function truncate(str, max = 28) {
  if (!str) return '—';
  const name = str.split('/').pop();
  return name.length > max ? name.slice(0, max) + '…' : name;
}

export default function History() {
  const navigate = useNavigate();
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tribe/battles`)
      .then(r => setBattles(r.data.battles || []))
      .catch(() => setError('Could not load your battle history. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      style={{ minHeight: '100vh', background: T.bg }}
    >
      {/* Nav */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 24px',
        borderBottom: `1px solid ${T.border}`,
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <a href="https://eonik.ai" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/logo.svg" alt="eonik" style={{ height: 18 }} />
        </a>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '6px 14px',
            background: T.fg,
            border: 'none',
            borderRadius: 100, cursor: 'pointer',
            color: '#fff',
            fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
        >
          <Plus size={11} /> NEW BATTLE
        </button>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '44px 24px 80px' }}>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: 9,
            letterSpacing: '0.14em', color: T.fgMuted,
            marginBottom: 6,
          }}>
            BATTLE HISTORY
          </p>
          <h1 style={{
            fontSize: 22, fontWeight: 700, color: T.fg,
            letterSpacing: '-0.02em', margin: '0 0 28px',
          }}>
            Past battles
          </h1>

          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Skeleton /><Skeleton /><Skeleton />
            </div>
          )}

          {!loading && error && (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 16, padding: '48px 0',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: 'var(--font-mono)', fontSize: 11,
                color: '#DC2626', letterSpacing: '0.04em',
              }}>
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '10px 20px', borderRadius: 10,
                  border: `1px solid ${T.border}`,
                  background: '#fff', color: T.fg,
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  letterSpacing: '0.05em', cursor: 'pointer',
                }}
              >
                REFRESH
              </button>
            </div>
          )}

          {!loading && !error && battles.length === 0 && (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 16, padding: '64px 0',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: 'var(--font-mono)', fontSize: 12,
                color: T.fgMuted, letterSpacing: '0.06em',
              }}>
                No tests yet.
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '10px 24px', borderRadius: 10,
                  border: `1px solid ${T.border}`,
                  background: T.fg, color: '#fff',
                  fontFamily: 'var(--font-mono)', fontSize: 12,
                  letterSpacing: '0.06em', cursor: 'pointer',
                }}
              >
                START A BATTLE →
              </button>
            </div>
          )}

          {!loading && battles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {battles.map((b, i) => {
                const v   = b.winner ? VERDICT[b.winner] : null;
                const date = new Date(b.created_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                });

                return (
                  <motion.div
                    key={b.battle_id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22, delay: i * 0.04 }}
                    onClick={() => navigate(`/battle/${b.battle_id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '13px 16px',
                      background: T.bgCard,
                      border: `1px solid ${T.border}`,
                      borderRadius: 12, cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#D4D4D8';
                      e.currentTarget.style.background = '#F4F4F5';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = T.border;
                      e.currentTarget.style.background = T.bgCard;
                    }}
                  >
                    {/* Verdict badge */}
                    <div style={{ flexShrink: 0, width: 60, display: 'flex', justifyContent: 'center' }}>
                      {v ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 7px', borderRadius: 5,
                          fontFamily: 'var(--font-mono)', fontSize: 9,
                          fontWeight: 800, letterSpacing: '0.05em',
                          background: v.bg, border: `1px solid ${v.border}`,
                          color: v.color,
                          whiteSpace: 'nowrap',
                        }}>
                          {v.label}
                        </span>
                      ) : (
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: T.fgMuted }}>
                          —
                        </span>
                      )}
                    </div>

                    {/* Names */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        fontSize: 13, fontWeight: 500, color: T.fg,
                        margin: '0 0 4px', fontFamily: 'var(--font-sans)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {truncate(b.filename_a || b.label_a)}{' '}
                        <span style={{ color: T.fgMuted, fontWeight: 400, fontSize: 11 }}>vs</span>{' '}
                        {truncate(b.filename_b || b.label_b)}
                      </p>
                      <p style={{
                        fontFamily: 'var(--font-mono)', fontSize: 10,
                        color: T.fgMuted, margin: 0, letterSpacing: '0.02em',
                      }}>
                        {date}
                      </p>
                    </div>

                    <ChevronRight size={12} style={{ color: T.fgMuted, flexShrink: 0 }} />
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
